/**
 * --------------------------------------------------------------------------
 * CoreUI PRO data-grid engine/data-source.ts
 * License (https://coreui.io/pro/license/)
 * --------------------------------------------------------------------------
 */

import type { DataGridDataRequest, DataGridDataResponse } from '../types'

const FETCH_DEBOUNCE_MS = 100

export interface GridDataSourceContext {
  getRequest: () => DataGridDataRequest
  onError: (error: unknown) => void
  // The request is handed back so an appending consumer can tell a first page
  // (replace) from a follow-up one (append) without re-reading table state,
  // which may already have moved on.
  onLoad: (response: DataGridDataResponse, request: DataGridDataRequest) => void
  onLoadingChange: (loading: boolean) => void
  provider: (request: DataGridDataRequest) => Promise<DataGridDataResponse>
}

export interface GridDataSource {
  load: () => void
  dispose: () => void
}

export const createDataSource = (context: GridDataSourceContext): GridDataSource => {
  let requestId = 0
  let timeout: ReturnType<typeof setTimeout> | undefined

  return {
    load() {
      clearTimeout(timeout)
      // Invalidate at call time, not when the debounce fires - an older
      // in-flight response must not render inside the debounce window.
      const id = ++requestId
      timeout = setTimeout(async () => {
        context.onLoadingChange(true)

        try {
          const request = context.getRequest()
          const response = await context.provider(request)
          if (id !== requestId) {
            return
          }

          context.onLoad(response, request)
        } catch (error) {
          if (id !== requestId) {
            return
          }

          context.onError(error)
        } finally {
          if (id === requestId) {
            context.onLoadingChange(false)
          }
        }
      }, FETCH_DEBOUNCE_MS)
    },
    dispose() {
      clearTimeout(timeout)
      requestId++
    }
  }
}
